/**
 * useElasticsearchOptions
 * --------------------------------------------------------------------------
 * Single source of truth for "fetch options for a select dropdown". Handles
 * BOTH the ES aggregation endpoint and the REST API endpoint branch (e.g.
 * the Culture tree API). The component does not care which served the
 * response — both paths return the same `{ options, page, hasMore }` shape.
 *
 * Why this hook exists (the bug it kills)
 * --------------------------------------------------------------------------
 * The previous implementation manually managed `options`, `currentPage`,
 * `hasMore`, `searchQuery`, and a chain of useEffects that all called a
 * single `fetchResults` closure. There were two race-condition paths that
 * silently corrupted the dropdown state:
 *
 *   1. No request cancellation. Typing "Py" then "Pyt" fired two requests
 *      in parallel; if the first returned later, its results overwrote the
 *      second's via `setOptions(formattedOptions)`. Result: user types
 *      "Python", waits 1–2 s, dropdown reverts to the unfiltered list.
 *
 *   2. Effect-driven refetches with stale closures. The `activeFacets`
 *      effect read `searchQuery` (debounced state) at the time *the effect
 *      ran*, not the time the user typed. A parent re-render during the
 *      150 ms debounce window with a new object reference for `filters`
 *      or `apiFilters` caused `fetchResults` to be a new closure → effect
 *      fired with `searchQuery=""`, clobbering the filtered list.
 *
 * How RQ fixes both
 * --------------------------------------------------------------------------
 *  - `useInfiniteQuery` deep-compares the query key. Reference-unstable
 *    objects (`filters`, `facets`, `searchFields`, …) do NOT cause refetches
 *    as long as their *contents* are equal.
 *  - `placeholderData: keepPreviousData` keeps the last successful options
 *    visible while the next query loads — no flicker on each keystroke.
 *  - `AbortSignal` is threaded through to axios. When the query key changes
 *    (e.g. `query: "Py"` → `query: "Pyt"`), RQ aborts the prior request.
 *    That's the ONLY thing that prevents an old "Py" response from landing
 *    after the new "Pyt" one. The signal-aware axios interceptor in
 *    services/api.js suppresses the cancel error toast.
 *
 * Note on cache busting
 * --------------------------------------------------------------------------
 * axios appends `_t=<timestamp>` to GET params via a request interceptor.
 * RQ caches by query key, axios cache-busts at the network layer — they
 * don't fight, this is intentional.
 */

import { useInfiniteQuery, keepPreviousData } from '@tanstack/react-query';
import { ElasticsearchService } from '@client/services/elasticsearchService';
import axiosInstance from '../../services/api';

export const elasticsearchOptionsKeys = {
  all: ['elasticsearch-options'],
  query: (params) => ['elasticsearch-options', params],
};

export function useElasticsearchOptions({
  document,
  field,
  query = '',
  pageSize = 20,
  sortBy = 'count',
  sortOrder = 'desc',
  valueField = null,
  labelField = null,
  filters = null,
  facets = null,
  searchFields = null,
  includeSourceFields = null,
  apiEndpoint = null,
  apiFilters = null,
  enabled = true,
}) {
  // The key includes EVERY input that affects the response. RQ deep-compares
  // so unstable references are fine; what matters is value equality.
  const queryKey = elasticsearchOptionsKeys.query({
    document,
    field,
    query,
    pageSize,
    sortBy,
    sortOrder,
    valueField,
    labelField,
    filters,
    facets,
    searchFields,
    includeSourceFields,
    apiEndpoint,
    apiFilters,
  });

  return useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam = 1, signal }) =>
      apiEndpoint
        ? fetchFromApiEndpoint({
            apiEndpoint,
            apiFilters,
            query,
            valueField,
            labelField,
            signal,
          })
        : fetchFromElasticsearch({
            document,
            field,
            query,
            page: pageParam,
            pageSize,
            sortBy,
            sortOrder,
            valueField,
            labelField,
            filters,
            facets,
            searchFields,
            includeSourceFields,
            signal,
          }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.hasMore ? lastPage.page + 1 : undefined,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    enabled: enabled && (!!apiEndpoint || (!!document && !!field)),
  });
}

// ----------------------------------------------------------------------------
// Internal fetchers
// ----------------------------------------------------------------------------

async function fetchFromApiEndpoint({
  apiEndpoint,
  apiFilters,
  query,
  valueField,
  labelField,
  signal,
}) {
  const { data } = await axiosInstance.get(apiEndpoint, {
    params: { search: query || '', ...apiFilters },
    signal,
  });
  const results = data?.results || data?.data || [];
  return {
    options: results.map((item) => ({
      label: item?.[labelField] ?? item?.french_name,
      value: item?.[valueField] ?? item?.id,
      count: null,
      highlighted: false,
      source: null,
    })),
    page: 1,
    hasMore: false,
  };
}

async function fetchFromElasticsearch({
  document,
  field,
  query,
  page,
  pageSize,
  sortBy,
  sortOrder,
  valueField,
  labelField,
  filters,
  facets,
  searchFields,
  includeSourceFields,
  signal,
}) {
  // facets → backend `facet_<field>=v1,v2`
  const facetParams = {};
  if (facets && typeof facets === 'object') {
    Object.entries(facets).forEach(([facetField, vals]) => {
      if (!Array.isArray(vals) || vals.length === 0) return;
      facetParams[`facet_${facetField}`] = vals.join(',');
    });
  }

  // multi-field text search & extra _source paths
  const extraSearchParams = {};
  if (Array.isArray(searchFields) && searchFields.length > 0) {
    extraSearchParams.search_fields = searchFields
      .map((sf) => (typeof sf === 'string' ? sf : sf.field))
      .join(',');
  }
  if (Array.isArray(includeSourceFields) && includeSourceFields.length > 0) {
    extraSearchParams.include_source_fields = includeSourceFields.join(',');
  }

  const response = await ElasticsearchService.search(
    {
      document,
      field,
      q: query || '',
      page,
      size: pageSize,
      sort: sortBy,
      order: sortOrder,
      value_field: valueField,
      label_field: labelField,
      ...filters,
      ...facetParams,
      ...extraSearchParams,
    },
    { signal },
  );

  return {
    options: (response?.options || []).map((opt) => ({
      label: opt.label,
      value: opt.value,
      count: opt.count,
      highlighted: opt.highlighted,
      source: opt.source,
    })),
    page,
    hasMore: !!response?.pagination?.has_more,
  };
}